import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api, setToken, setStoredUser, getToken, getStoredUser } from '../utils/api';
import { getCurrentPayMonth } from '../utils/helpers';

const AppContext = createContext(null);

export function AppProvider({ children }) {
  const [user, setUser] = useState(getStoredUser());
  const [activeMonth, setActiveMonth] = useState(getCurrentPayMonth());
  const [isSimulating, setIsSimulating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // A dashboard left open across midnight on the 1st would otherwise keep
  // showing last month until someone reloaded the page. Simulation is left
  // alone — an admin who picked a month keeps it.
  useEffect(() => {
    const id = setInterval(() => {
      if (isSimulating) return;
      const current = getCurrentPayMonth();
      setActiveMonth(prev => (prev === current ? prev : current));
    }, 60000);
    return () => clearInterval(id);
  }, [isSimulating]);

  const login = useCallback(async (username, password) => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.login(username, password);
      setToken(data.token);
      setStoredUser(data.user);
      setUser(data.user);
      return data.user;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setStoredUser(null);
    setUser(null);
    setActiveMonth(getCurrentPayMonth());
    setIsSimulating(false);
  }, []);

  const switchMonth = useCallback((month) => {
    const current = getCurrentPayMonth();
    if (month === current || !month) {
      setActiveMonth(current);
      setIsSimulating(false);
    } else {
      setActiveMonth(month);
      setIsSimulating(true);
    }
  }, []);

  // For managers: always current month, no simulation
  const effectiveMonth = user?.role === 'manager' ? getCurrentPayMonth() : activeMonth;

  const value = {
    user,
    activeMonth: effectiveMonth,
    isSimulating: user?.role === 'manager' ? false : isSimulating,
    loading,
    error,
    login,
    logout,
    switchMonth,
    setError,
    setLoading,
    isAdmin: user?.role === 'admin',
    isHO: user?.role === 'manager-ho',
    isManager: user?.role === 'manager',
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
}
